import { Alert, Anchor, Button, Group, Stack } from '@mantine/core';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FiAlertCircle } from 'react-icons/fi';
import { Navigate, useNavigate } from 'react-router-dom';
import { landingPath, useAuth } from '@web/lib/auth';
import { errorMessage } from '@web/lib/errors';
import { useChangePassword } from './api';
import { AuthLayout } from './AuthLayout';
import { MfaEnroll } from './MfaEnroll';
import { NewPasswordField } from './NewPasswordField';
import { CurrentPasswordField } from './PasswordPages';

/** Forced first steps: change the password, then set up two-factor authentication. */
export function SetupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me, reload, signOut } = useAuth();
  const change = useChangePassword();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [valid, setValid] = useState(false);

  if (!me) return <Navigate to="/login" replace />;
  if (!me.user.mustChangePassword && !me.user.mfaSetupRequired) return <Navigate to={landingPath(me)} replace />;

  const signOutLink = (
    <Group justify="center" mt="md">
      <Anchor component="button" type="button" size="sm" onClick={() => void signOut()}>
        {t('shell.signOut')}
      </Anchor>
    </Group>
  );

  if (me.user.mustChangePassword) {
    return (
      <AuthLayout title={t('auth.setup.passwordTitle')} subtitle={t('auth.setup.passwordSubtitle')}>
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            change.mutate({ currentPassword: current, newPassword: next });
          }}
          noValidate
        >
          <Stack gap="md">
            {change.isError && (
              <Alert color="red" icon={<FiAlertCircle />}>
                {errorMessage(change.error)}
              </Alert>
            )}
            <CurrentPasswordField value={current} onChange={setCurrent} />
            <NewPasswordField value={next} onChange={setNext} onCheck={(c) => setValid(c.valid)} email={me.user.email} />
            <Button type="submit" fullWidth loading={change.isPending} disabled={!valid || !current}>
              {t('auth.setup.passwordSubmit')}
            </Button>
          </Stack>
        </form>
        {signOutLink}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.setup.mfaTitle')} subtitle={t('auth.setup.mfaSubtitle')}>
      <MfaEnroll
        submitType="submit"
        onDone={async () => {
          const fresh = await reload();
          if (fresh) navigate(landingPath(fresh), { replace: true });
        }}
      />
      {signOutLink}
    </AuthLayout>
  );
}
